import React, { useEffect, useRef, useState } from "react";

import "../../App.scss";

import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import ProjectMetadata, { UserReference } from "../../models/ProjectMetadata";
import { ApplicationMode } from "../../models/SettingsEnums";

import { createEmptyProject } from "../../DemoData";
import HeadBar from "../HeadBar";
import universalstorageHandler from "../../persistence/UniversalstorageHandler";
import { setSession } from "../account/auth-helper";
import ZipStorageHandler from "../../persistence/ZipStorageHandler";
import { newId } from "../../models/BaseObject";
import ProjectCard from "./ProjectCard";
import { saveProject } from "../SaveProject";
import BaseDialog from "../../modelDialogs/BaseDialog";
import WaitDialog from "../WaitDialog";

import ExampleProjectCard from "./ExampleProjectCard";
import { askToSaveProjectBeforeLeaving } from "../../components/dialogs/SaveProjectBeforeLeaving";

import { useNavigate } from "react-router-dom";
import { allignColumn } from "../../modelViews/GraficElementUtils";
import { encrypt, updateUserDetails } from "../account/Login/Login";
import { enumUiMode, isBetaVersion, isUiModeAdmin, isUiModeDeveloper, setMaxUiMode } from "../../uiMode";

import ProjectHandleling from "./ProjectHandeling";
import { usePreventZoom } from "../UsePreventZoom";
import Browser from "../../browser";
import GPTProjectCard from "./GPTProjectCard";
import { showToast, updateToastProgress } from "../Toasts";
import outPutAllSignalTypesWithUsageOfChannelTypes from "./extraFunctionsForDeveloper";
import { itx, noItx } from "../../translationItx/itx";

import axios, { getBaseUrl } from "../../utils/axios";
import { isDevelopmentEnvironment } from "../../estada-config";
import ServerDataBaseHandler from "../../persistence/SeverDataBaseHandler";
import GlobalWaitIndicator, { showGlobalWaitIndicator } from "../GlobalWaitIndicator";
import { Loading } from "../EstadaSuspense";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import Logger from "../../Logger";
import FirefoxAlert from "../FirefoxAlert";
import VerificationProjectCard from "./VerificationProjectCard";
import getExtensionInstance from "../../extensions/getExtensionInstance";
import ProjectLicense from "../../models/ProjectLicense";
import { EventKey } from "../../eventLogger/EventKeys";
import { loadProject } from "../LoadProject";
import ConfirmDialog from "../../modelDialogs/ConfirmDialog";
import ConceptOneProjectCard from "./ConceptOneProjectCard";
import { SHOW_CONCEPT_ONE_ACCESS } from "./projectSelectionFeatures";
import FullSceenButton from "../FullscreenButton";

const logger = new Logger();

type SortMode = "favorite" | "name" | "date" | "numberButtons" | "numberActors" | "numberDevices";

export default function ProjectSelection(props: any) {
  const projectHandleling = new ProjectHandleling();
  //const [allProjects,setAllProjects]=useState<Map<string,ProjectMetadata>>(state.allProjectsMap)
  const [projects, setProjects] = useState(new Array<ProjectMetadata>());
  const fetchProjectsRunning = useRef<boolean>(false);
  const [openNewProject, setOpenNewProject] = useState(false);

  const [uploadRunning, setUploadRunning] = useState(false);
  const [projectsLoaded, setprojectsLoaded] = useState(false);

  const [expandedProjects, setExpandedProjects] = useState<string>("");
  const [projSearchValue, setProjSearchValue] = useState("");
  const [projSearchNameOrEmailValue, setProjSearchNameValue] = useState("");
  // Keep the active sort mode in state so reloads and favorite changes preserve the current order.
  const [sortMode, setSortMode] = useState<SortMode>(getSortModeFromLocalStorage());
  const [serverIsOnline, setServerIsOnline] = useState(true);
  const [recoveryBackups, setRecoveryBackups] = useState<ProjectMetadata[]>([]);
  const [recoveryCheckComplete, setRecoveryCheckComplete] = useState(false);
  const [recoveryDismissed, setRecoveryDismissed] = useState(false);
  const recoveryCheckRunning = useRef(false);

  const [userVerified, setUserVerified] = useState(state.loggedInUser?.is_active || false);

  const loginIsRunning = useRef<boolean>(false);

  const navigate = useNavigate();

  const isMobile = Browser.isMobile();

  useEffect(() => {
    state.eventLogger.startSession(state.loggedInUser?.id ?? state.loggedInUser?.email ?? "", "project_selection");
    state.eventLogger.addEvent(EventKey.ProjectSelectionOpened, { selection: "estada" });
  }, []);

  function getSortModeFromLocalStorage(): SortMode {
    const storedSortMode = localStorage.getItem("projectSelectionSortMode") as SortMode;
    if (storedSortMode) {
      return storedSortMode;
    }
    return "favorite"; // no itx
  }
  // Initializes login state, connectivity state and the first project load.
  async function init() {
    //
    // only for easier debugging
    //
    if (isDevelopmentEnvironment()) {
      const importFromEtsProjectFile = (await import("../../extensions/imports/ets/fileHandling")).default;
    }

    state._navigate = navigate;

    // setback licenses
    state.projectLicense = new ProjectLicense();

    // get sort mode from local storage to persist it across sessions
    const storedSortMode = localStorage.getItem("projectSelectionSortMode") as SortMode;
    if (storedSortMode) {
      setSortMode(storedSortMode);
    }

    //
    // loads and sorts projects form state
    //
    sortProjects();

    await showGlobalWaitIndicator(
      true,
      itx("homePage.projectsLoadingWait", "Projekte werden geladen ... Bitte warten").split("^")[0],
    );

    const online = await ServerDataBaseHandler.serverIsOnline();

    setServerIsOnline(online);
    universalstorageHandler.temporaryOffline = !online;
    universalstorageHandler.updateStorageHandler();

    if (
      state.isDirty &&
      !window.location.origin.endsWith("localhost:3000") &&
      !window.location.origin.endsWith("estada.de")
    ) {
      askToSaveProjectBeforeLeaving();
    } else {
      if (state.dialogManager.isOpen) {
        logger.error("Dialog is open");
        state.dialogManager.closeDialog();
      }

      if (!state.isLoggedIn) {
        if (!loginIsRunning.current) {
          const user = state.loggedInUser;
          loginIsRunning.current = true;
          const res = await universalstorageHandler.login(user);

          loginIsRunning.current = false;
          if (state?.loggedInUser?.abo) {
            // coudl be Personal, Pro, Elite
            state.loggedInUser.abo = res?.abo;
            state.loggedInUser.abo_end = res?.abo_end;
            state.loggedInUser.abo_status = res?.abo_status;
          }

          if (isDevelopmentEnvironment() && res) {
            //res.abo = "development"; // no itx
          }

          if (!state.loggedInUser || res == undefined) {
            //set user to null to avoid problems with wrong user data in local storage
            localStorage.setItem("user", "");
            state.navigate("/login");
            // } else if (!AboHandeling.aboValid(res.abo, res.abo_end, res.abo_status)) { because of
            //   window.location.href = "https://estada.de/pricing";
          } else {
            setSession(res.token);
            state.isLoggedIn = true;
            (window as any).isEstadaLoggedIn = true;
            await fetchProjects();
          }
        }
      } else {
        await fetchProjects();
      }
    }

    document.title = `ESTADA`;
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    // setback licenses
    state.projectLicense = new ProjectLicense();
    if (openNewProject) {
      state.dialogManager.openDialog(<NewProjectDialog />);
    }
  }, [openNewProject]);

  useEffect(() => {
    allignColumn("projectSelectionCard");
  });

  useEffect(() => {
    // save sort mode to local storage to persist it across sessions if not already the value
    const storedSortMode = localStorage.getItem("projectSelectionSortMode") as SortMode;
    if (storedSortMode !== sortMode) {
      localStorage.setItem("projectSelectionSortMode", sortMode);
    }
  }, [sortMode]);

  useOnChangeWatcher(state.settings);

  usePreventZoom();

  // Reserved for refreshing subscription data from the backend when needed.
  async function updateUserAbo() {
    try {
      const payload = {
        email: state.loggedInUser.email,
        password: encrypt(state.loggedInUser.password),
      };
      // axios
      //   .post("/api/login", payload)
      //   .then(async (result: any) => {
      //     if (result.data.DATA?.token) {
      //       // redirect to APP Dashboard
      //       // check if abo is changed
      //     }
      //   })
      //   .catch((e) => {
      //     console.log("error");
      //   });
    } catch (ex) {
      logger.error(ex);
    }
  }

  function sortProjects(nextSortMode: SortMode = sortMode) {
    let res = Array.from(state.allProjectsMap.values());

    // Favorites are derived from persisted user settings, not stored directly on the project.
    const favoriteSelectedProjects = state.settings.getFavoriteSelectedProjects();
    const isFavoriteProject = (project: ProjectMetadata) => favoriteSelectedProjects.indexOf(project.id) != -1;
    res.forEach((item: any) => {
      item.isFavorit = isFavoriteProject(item);
    });

    // All sorting variants are centralized here so refreshes use the same active mode.
    res = res.sort((a: ProjectMetadata, b: ProjectMetadata) => {
      const nameA = (a.name as string)?.toLocaleLowerCase() || "";
      const nameB = (b.name as string)?.toLocaleLowerCase() || "";

      switch (nextSortMode) {
        case "date": {
          const lastModifiedDiff = b.lastModifiedOnServer.getTime() - a.lastModifiedOnServer.getTime();
          if (lastModifiedDiff != 0) {
            return lastModifiedDiff;
          }
          return nameA.localeCompare(nameB);
        }
        case "name":
          return nameA.localeCompare(nameB);
        case "numberButtons": {
          const diff = (b.statistic?.numberOfButtons || 0) - (a.statistic?.numberOfButtons || 0);
          if (diff != 0) {
            return diff;
          }
          return nameA.localeCompare(nameB);
        }
        case "numberActors": {
          const diff = (b.statistic?.numberOfActors || 0) - (a.statistic?.numberOfActors || 0);
          if (diff != 0) {
            return diff;
          }
          return nameA.localeCompare(nameB);
        }
        case "numberDevices": {
          const numberOfDevicesA = (a.statistic?.numberOfButtons || 0) + (a.statistic?.numberOfActors || 0);
          const numberOfDevicesB = (b.statistic?.numberOfButtons || 0) + (b.statistic?.numberOfActors || 0);
          const diff = numberOfDevicesB - numberOfDevicesA;
          if (diff != 0) {
            return diff;
          }
          return nameA.localeCompare(nameB);
        }
        case "favorite":
        default:
          if (isFavoriteProject(a) && !isFavoriteProject(b)) {
            return -1;
          }
          if (!isFavoriteProject(a) && isFavoriteProject(b)) {
            return 1;
          }
          return nameA.localeCompare(nameB);
      }
    });
    setProjects(res);
  }

  function setSortAndApply(nextSortMode: SortMode) {
    // Update state and list together to avoid the UI showing a stale sort mode.
    setSortMode(nextSortMode);
    sortProjects(nextSortMode);
  }

  // Loads projects from cache/server and rebuilds the in-memory project map.
  async function fetchProjects(fromDelete?: boolean) {
    if (state.allProjectsMap.size != 0 || fromDelete) {
      sortProjects();
      setprojectsLoaded(true);
    }

    if (!fetchProjectsRunning.current) {
      fetchProjectsRunning.current = true;
      const sUser = localStorage.getItem("user");

      if (sUser && sUser != "") {
        const user = JSON.parse(sUser);
        const email = user.email;

        if (user.rights?.mode) {
          setMaxUiMode(user.rights.mode);
        } else {
          setMaxUiMode(enumUiMode.user);
        }

        if (isDevelopmentEnvironment()) {
          setMaxUiMode(enumUiMode.developer);
        }

        if (email) {
          outPutAllSignalTypesWithUsageOfChannelTypes();
        }

        if (!recoveryCheckRunning.current) {
          recoveryCheckRunning.current = true;
          const autobackups = await universalstorageHandler.localStorageHandler.loadLatestAutobackups();
          setRecoveryBackups(
            autobackups.map((data: any) => {
              const metadata = new ProjectMetadata();
              metadata.fromJSON(data);
              return metadata;
            }),
          );
          setRecoveryCheckComplete(true);
        }

        const res = await universalstorageHandler.loadProjectList(email);
        try {
          logger.log(`projects loaded ${res.length}`);

          const allProjectsMap = new Map<string, ProjectMetadata>();
          const allSnapshotsProjectsArray = new Array<ProjectMetadata>();
          res.forEach((data: any) => {
            // handle here snapshots

            const projectMetadata = new ProjectMetadata();
            projectMetadata.fromJSON(data);

            if (data.id.includes("-snapshot")) {
              allSnapshotsProjectsArray.push(projectMetadata);
            } else {
              allProjectsMap.set(projectMetadata.id, projectMetadata);
            }
          });
          allSnapshotsProjectsArray.forEach((snapshotProject) => {
            let newestProjectId = "";
            const p = snapshotProject.id.indexOf("-snapshot");
            if (p > 0) {
              newestProjectId = snapshotProject.id.substring(0, p);
            }
            const newestProject = allProjectsMap.get(newestProjectId);
            if (newestProject) {
              newestProject.snapshots.push(snapshotProject);
            }
          });

          state.allProjectsMap = allProjectsMap;

          await ensureDummyAboLicenseAssigned();

          //state.allProjects = res;
          sortProjects();
          setprojectsLoaded(true);
        } catch (ex) {
          logger.error(ex);

          fetchProjectsRunning.current = false;
        }
      }
      showGlobalWaitIndicator(false);
    }
  }

  async function ensureDummyAboLicenseAssigned() {
    // Dummy subscriptions should automatically grant one project license if none exists yet.
    // Dummy subscriptions should automatically grant one project license if none exists yet.
    const abo = state.loggedInUser?.abo?.toLowerCase() || "";
    if (!abo.includes("dummy")) {
      return;
    }

    if (userHasDummyAboLicense()) {
      return;
    }

    const projectsArray = Array.from(state.allProjectsMap.values());
    if (projectsArray.length === 0) {
      return;
    }

    const hasDummyLicense = projectsArray.some((project) => {
      const invoiceIds = project.projectLicence?.invoiceIds || [];
      return invoiceIds.some((id) => (id || "").toLowerCase() === "dummyabo");
    });

    if (hasDummyLicense) {
      return;
    }

    const userEmail = state.loggedInUser?.email?.toLowerCase() || "";
    const preferredProject = projectsArray.find((project) => {
      const ownerEmail = project.users?.[0]?.email?.toLowerCase() || "";
      return (
        !project.readOnly &&
        (!project.projectLicence || project.projectLicence.invoiceIds.length === 0) &&
        ownerEmail === userEmail
      );
    });
    if (!preferredProject) {
      return;
    }

    if (!preferredProject.projectLicence) {
      preferredProject.projectLicence = { invoiceIds: [] };
    }

    const invoiceIds = preferredProject.projectLicence.invoiceIds || [];
    preferredProject.projectLicence.invoiceIds = [...invoiceIds, "dummyAbo"];

    await addDummyAboLicenseToUser(preferredProject.id);
    await universalstorageHandler.saveProjectMetaData(preferredProject);
  }

  function userHasDummyAboLicense(): boolean {
    // License information can arrive in multiple backend shapes, so both are checked.
    const user: any = state.loggedInUser;
    if (!user) {
      return false;
    }

    const dummyKey = "dummyabo";
    const singleKey = typeof user.licenseKey === "string" ? user.licenseKey : "";
    if (singleKey.toLowerCase().includes(dummyKey)) {
      return true;
    }

    const keyList = Array.isArray(user.license_key) ? user.license_key : [];

    return keyList.some((item: any) => {
      if (typeof item === "string") {
        return item.toLowerCase().includes(dummyKey);
      }
      const key = item?.licenseKey;
      return typeof key === "string" && key.toLowerCase().includes(dummyKey);
    });
  }

  async function addDummyAboLicenseToUser(projectId: string) {
    // Creates a backend license entry that matches the locally assigned dummy project license.
    const userId = state.loggedInUser?.id;
    if (!userId) {
      return;
    }

    try {
      await axios.post(getBaseUrl() + "/api/chargebee/process-payment", {
        id: userId,
        licenseKey: "dummyAbo" + projectId, // to make it possible to have multiple dummyAbo licenses for one user if they have multiple projects without license
      });
    } catch (error) {
      logger.error("Failed to add dummyAbo license to user", error);
    }
  }

  // Renders the card for creating or importing projects.
  function NewProjectCard() {
    let fileName = "";

    // Starts reading the selected import file into memory.
    function importFile(event: any) {
      const file = event.target.files[0];
      fileName = file.name;
      const fr = new FileReader();
      fr.onload = receivedBlob;
      // fr.readAsText(file);
      fr.readAsArrayBuffer(file);
    }

    // function receivedText(e: any) {
    //   let lines = e.target.result;
    //   var newArr = JSON.parse(lines);
    //   logger.log("newArr ", newArr);
    //   newArr.id = Guid.create().toString();
    //   alert("Import project is Disabled");
    //   // dbHandler.importToDB(newArr);
    // }

    // Imports a .estada archive, duplicates metadata safely and opens the imported project.
    async function receivedBlob(e: any) {
      const blob = new Blob([e.target.result]);

      const z = new ZipStorageHandler();
      await z.setBlob(blob);

      state.clear();

      const projecMetadataJson = await z.loadProjectMetadata("");
      if (projecMetadataJson) {
        state.projectMetadata.fromJSON(projecMetadataJson);
      } else {
        state.projectMetadata.name = "Import from: " + fileName;
      }

      const ok = await z.loadProjectItemsFromZip(state.projectMetadata.id);
      await z.loadDeletedIds();

      const date = new Date();

      let newProjectName =
        projecMetadataJson?.name +
        ` ( ${itx("appPage.imported", "importiert")} ${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()})`;
      const projectnames = new Array<string>();
      state.allProjectsMap.forEach((project) => {
        if (project.id != state.projectMetadata.id) {
          projectnames.push(project.name);
        }
      });
      while (state.noDoubleValue.doubleName(newProjectName, projectnames)) {
        newProjectName = state.noDoubleValue.getCopiedStringText(newProjectName);
      }

      state.projectMetadata.name = newProjectName;
      state.projectMetadata.id = newId();
      state.root.id = state.projectMetadata.id;
      state.projectMetadata.users = new Array<UserReference>();
      state.projectMetadata.setUserForNewProject();

      state.projectMetadata.lastModifiedOnServer = new Date();

      if (!isDevelopmentEnvironment()) {
        saveProject();
      }
      // get knxschema via user?
      if (state.loggedInUser?.onboarding_quiz) {
        for (const [key, value] of Object.entries(state.loggedInUser.onboarding_quiz)) {
          if (key.includes("schema") && (value == "a" || value == "b" || value == "c")) {
            state.settings.knxGroupAddressGeneneration = (value as string).toUpperCase() as "A" | "B" | "C";
          }
        }
      }

      state.navigate("/app");
    }

    // Shared inline layout for the action buttons on the new-project card.
    // Shared inline layout for the action buttons on the new-project card.
    const style: any = {
      display: "flex",
      flexDirection: "row",
      gap: "3px",
      alignItems: "center",
    };
    return (
      <div
        key={"p-new"} // no itx
        className={"projectSelectionCard"} // no itx
        style={{ backgroundColor: "var(--primary-color-light)" }}
      >
        <CardContent>
          <Typography
            variant="h5" // no itx
            component="div"
          >
            {state.translate("homePage.createProject", "Create New KNX Project")}
          </Typography>
        </CardContent>
        <CardActions
          style={{
            justifyContent: "center",
          }}
        >
          <button
            style={style}
            className="button"
            onClick={() => {
              state.navigate("/quickstart");
            }}
          >
            {/* <i className={`las la-hands-helping`}></i> */}
            {state.translate("homePage.withAssistant", "With Assistant")}
          </button>

          {!isMobile && (
            <button
              style={style}
              className="button"
              onClick={() => {
                setOpenNewProject(true);
              }}
            >
              {/* <i className={`las la-plus-square`}></i> */}
              {state.translate("homePage.emptyProject", "Empty Project")}
            </button>
          )}

          {!isMobile && (
            <label
              className="button labelButton"
              htmlFor="file"
              data-tooltip-id="tv-tooltip"
              data-tooltip-html={
                isBetaVersion()
                  ? "Import:\n * ESTADA-Backup (.estada) oder \n  * KNX-Projekte (.knxproj)".replaceAll("\n", "<br/>")
                  : ""
              }
              data-tooltip-place="bottom"
            >
              <input
                style={{ display: "none" }}
                id="file"
                name="file"
                type="file"
                accept={".estada" + (isBetaVersion() ? ",.knxproj" : "")}
                onChange={async (event: any) => {
                  const file = event.target.files[0];
                  const fileName = file.name.toLowerCase();
                  if (fileName.endsWith(".estada")) {
                    importFile(event);
                  } else if (fileName.endsWith(".knxproj")) {
                    const etsImport = await getExtensionInstance("ets_import");
                    etsImport && (await etsImport.execute(event));

                    state.navigate("/app");
                  }
                }}
              />
              {/* <i className={`las la-file-import`}></i> */}
              {state.translate("homePage.import", "Import: ESTADA-Backup (.estada) oder KNX-Projekte (.knxproj)")}
            </label>
          )}

          {isDevelopmentEnvironment() && (
            <button
              style={style}
              className="button"
              data-tooltip-id="tv-tooltip"
              data-tooltip-content={itx("testProject.name", "Test Project")}
              data-tooltip-place="bottom"
              onClick={(ev) => {
                // copyTestProjectFromServer
                const newName = itx("testProject.name", "Test Project") + " " + new Date().toLocaleTimeString();

                projectHandleling.copyProject(
                  "f86925bd-78f4-4730-d803-33c42a78feea", // no itx
                  ev,
                  navigate,
                  state.translate(
                    "exampleProjectCard.exampleProjectDescriptionAfterLoading",
                    "Entdecke alle Funktionen anhand eines Beispiels",
                  ),
                  false,
                  newName,
                );
              }}
            >
              +
            </button>
          )}
        </CardActions>

      </div>
    );
  }

  // Dialog for creating a brand-new project with name and description.
  function NewProjectDialog(props: { nam?: string; desc?: string }) {
    const { nam, desc } = props;
    const [name, setName] = useState(nam || "");
    const [description, setDescription] = useState(desc || "");

    // Builds a new empty project and forwards the user into the editor.
    function clickCreateProject() {
      if (!navigator.onLine) {
        showToast({
          isError: true,
          text: state.translate(
            "loginPage.connectionInternetNotAailiable",
            "Die Verbindung zum Server kann nicht hergestellt werden. Bitte überprüfen Sie Ihr Internet.",
          ),
        });
        return;
      }
      logger.log("name, description", name, description);

      const project = new ProjectMetadata();
      project.setUserForNewProject();

      project.name = name;
      project.description = description;
      state.projectMetadata = project;

      // createDemoData(state);
      createEmptyProject(state);

      if (!isDevelopmentEnvironment()) {
        saveProject();
      }
      state.navigate("/app");
      state.eventLogger.startSession(
        state.loggedInUser?.id ?? state.loggedInUser?.email ?? "",
        state.projectMetadata.id,
      );
      state.eventLogger.addEvent(EventKey.ProjectCreated, {
        projectId: state.projectMetadata.id,
        creationType: "Manual", // no itx
      });
    }

    return (
      <div>
        <BaseDialog
          title={state.translate("homePage.createProject", "Neues KNX Projekt anlegen")}
          onOk={() => {
            if (name != "") {
              const names: Array<string> = [];

              if (!state.noDoubleValue.doubleName(name, names)) {
                if (projects) {
                  projects.map((project: any, index: any) => {
                    names.push(project.name);
                  });
                }
                clickCreateProject();
                return true;
              } else {
                state.dialogManager.openDialog(
                  <BaseDialog
                    title={state.translate("step0.projectNameAlreadyUsed", "Projektname schon vorhanden")}
                    onOk={() => {
                      state.dialogManager.openDialog(<NewProjectDialog desc={description} />);
                      return false;
                    }}
                    okCaption={state.translate("ok", "Ok")}
                    noAbortButton={true}
                  >
                    <div style={{ padding: "20px" }}>
                      {state.translate(
                        "step0.projectNameAlreadyUsedText",
                        "Sie haben einen schon vorhandenen Namen eingegeben! Bitte geben sie einen neuen ein um fort zu führen",
                      )}
                    </div>
                  </BaseDialog>,
                );

                return false;
              }
            } else {
              state.dialogManager.openDialog(
                <BaseDialog
                  title={state.translate("step0.projectName?", "Projektname?")}
                  onOk={() => {
                    state.dialogManager.openDialog(<NewProjectDialog desc={description} />);
                    return false;
                  }}
                  okCaption={state.translate("ok", "Ok")}
                  noAbortButton={true}
                >
                  <div style={{ padding: "20px" }}>
                    {state.translate(
                      "step0.noNameentered",
                      "Sie haben keinen Namen eingegeben! Bitte geben sie einen ein um fort zu führen",
                    )}
                  </div>
                </BaseDialog>,
              );
              return false;
            }
          }}
          onClose={() => {
            setOpenNewProject(false);
            return true;
          }}
        >
          <div style={{ padding: "20px" }}>
            <tbody>
              <tr>
                <td>{state.translate("name", "Name")}:</td>
                <td style={{ paddingRight: "5px" }}>
                  <input
                    id="projectName"
                    className="input"
                    placeholder={state.translate("coverPage.projectName", "Projektname")}
                    type={"text"}
                    value={name}
                    maxLength={state.maxCharLimit.text}
                    autoFocus={true}
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ paddingRight: "5px" }}>
                  {state.translate("listComponents.actorProductDescription", "Beschreibung")}:
                </td>
                <td>
                  <input
                    className="input"
                    type={"text"}
                    value={description}
                    maxLength={state.maxCharLimit.largeText}
                    onChange={(e) => {
                      setDescription(e.target.value);
                    }}
                  />
                </td>
              </tr>
            </tbody>
          </div>
        </BaseDialog>
      </div>
    );
  }

  // Persists favorite changes and reapplies the currently active sorting mode.
  function setFavoriteProject(id: string) {
    const favoriteSelectedProjects = state.settings.getFavoriteSelectedProjects();
    const index = favoriteSelectedProjects.indexOf(id);
    if (index != -1) {
      favoriteSelectedProjects.splice(index, 1);
    } else {
      favoriteSelectedProjects.push(id);
    }
    state.settings.setFavoriteSelectedProjects(favoriteSelectedProjects);
    updateUserDetails({
      favouritedProjects: JSON.stringify(favoriteSelectedProjects),
    });

    sortProjects();
  }

  // Only one project card can be expanded at a time.
  function setExpandedProject(id: string) {
    if (expandedProjects == id) {
      setExpandedProjects("");
    } else {
      setExpandedProjects(id);
    }
  }

  // Filters the visible cards by project name/id and, for admins, by owner name/email.
  function displaySearchProject(project: any) {
    // if nothing is written in neither search input
    if (projSearchValue == "" && projSearchNameOrEmailValue == "") {
      return true;
    }

    if (projSearchValue != "" && project?.name?.toLowerCase().includes(projSearchValue?.toLowerCase())) {
      return true;
    }
    // to search after project id
    if (projSearchValue != "" && project?.id?.toLowerCase().includes(projSearchValue?.toLowerCase())) {
      return true;
    }

    if (projSearchNameOrEmailValue != "") {
      if (project.users.length > 0) {
        const projectUser = project.users[0];
        const nameSearchValue = projSearchNameOrEmailValue.toLowerCase();

        const isMatchingUsername = projectUser?.name?.toLowerCase().includes(nameSearchValue);
        const isMatchingEmail = projectUser?.email?.toLowerCase().includes(nameSearchValue);

        return isMatchingUsername || isMatchingEmail;
      }
    }
    return false;
  }

  // // set the Draged Project
  // const handleDragProjectCard = (ev: React.DragEvent<HTMLDivElement>) => {
  //   setDragId(ev.currentTarget.id);
  // };
  // //Drop Project on destined part und sort new
  // const handleDropProjectCard = (ev: React.DragEvent<HTMLDivElement>) => {
  //   const dragProject = projects.find((project) => project.id === dragId);
  //   const dropProject = projects.find(
  //     (project) => project.id === ev.currentTarget.id,
  //   );
  //   // sort the order new
  //   const newOrder = new Array<string>();
  //   if (order) {
  //     var dragProjectPlaceDone = false;
  //     for (let i = 0; i < order.length; i++) {
  //       //drop place
  //       if (dropProject.id == order[i]) {
  //         if (dragProjectPlaceDone) {
  //           newOrder.push(order[i]);
  //           newOrder.push(dragProject.id);
  //         } else {
  //           newOrder.push(dragProject.id);
  //           newOrder.push(order[i]);
  //         }
  //       } else {
  //         if (dragProject.id != order[i]) {
  //           newOrder.push(order[i]);
  //         } else {
  //           dragProjectPlaceDone = true;
  //         }
  //       }
  //     }
  //     setOrder(newOrder);
  //   }
  // };

  // Convenience wrappers used by the admin sort select.
  function sortAfterName() {
    setSortAndApply("name"); // no itx
  }

  function sortByDate() {
    setSortAndApply("date"); // no itx
  }
  function sortNumberDevices(className?: string) {
    if (className == "Button") {
      setSortAndApply("numberButtons"); // no itx
      return;
    }
    if (className == "Actor") {
      setSortAndApply("numberActors"); // no itx
      return;
    }
    setSortAndApply("numberDevices"); // no itx
  }
  // Options for the admin-only sort select; non-admin users only toggle date/favorite directly.
  const sortOptions = new Array<any>();
  const sortElements = new Map<string, { text: string; onChange: () => void }>();
  sortElements.set(
    "favorite", // no itx
    {
      text: itx("ProjectSelection.sortfavoritefirst", "sort favorite first"),
      onChange: () => {
        setSortAndApply("favorite"); // no itx
      },
    },
  ); // no itx
  sortElements.set("name", { text: itx("ProjectSelection.sortaftername", "sort after name"), onChange: sortAfterName }); // no itx
  sortElements.set(noItx("date"), { text: itx("ProjectSelection.sortbydate", "sort by date"), onChange: sortByDate });
  sortElements.set(
    "numberButtons", // no itx
    {
      text: itx("homePage.sortCountButtons", "sort count buttons"),
      onChange: () => {
        sortNumberDevices("Button"); // no itx
      },
    },
  );
  sortElements.set(
    "numberActors", // no itx
    {
      text: itx("homePage.sortCountActors", "sort count actors"),
      onChange: () => {
        sortNumberDevices("Actor"); // no itx
      },
    },
  );
  sortElements.set(
    "numberDevices", // no itx
    {
      text: itx("homePage.sortCountDevice", "sort count device"),
      onChange: () => {
        sortNumberDevices();
      },
    },
  );
  sortElements.forEach((value, key) => {
    sortOptions.push(
      <option key={key} value={key}>
        {value.text}
      </option>,
    );
  });
  ////////////////////////////////////////////////////////////////////////////
  /// abo
  const abo = state.loggedInUser?.abo;

  function showNewProjectCard(): boolean {
    // The guard is currently bypassed, but the original subscription logic is kept below.
    return true;
    if (
      projects.filter((pm) => !pm.readOnly).length >= 1 &&
      ((abo as string)?.includes("personal") || (abo as string)?.includes("dummy") || (abo as string) == "no") // no itx
    ) {
      return false;
    }
    return true;
  }

  // Developer-only maintenance buttons for downloading user mail reports.
  function DownloadEmailsButtons(props: {}) {
    return (
      <div>
        <button
          style={{ zIndex: "100", position: "absolute", right: "0px", bottom: "0px" }}
          className="button"
          onClick={() => {
            const z = new ServerDataBaseHandler();
            z.calcMails("muellMailUsers"); //calcMuellMailUsers();  // no itx
          }}
        >
          {noItx("download MüllmailUsers")}
        </button>

        <button
          style={{ zIndex: "100", position: "absolute", right: "0px", bottom: "35px" }}
          className="button"
          onClick={() => {
            const z = new ServerDataBaseHandler();
            z.calcMails("UsersWithoutPojects"); // no itx
          }}
        >
          {noItx("check user without projects")}
        </button>

        {/* <button
          style={{ zIndex: "100", position: "absolute", right: "0px", bottom: "100px" }}
          className="button"
          onClick={() => {
            const actions = [
              {
                id: "1",
                caption: "Lösche Verbindung zu: " + "1",
                action: () => {
                  showToast({ text: itx("index.action1", "action 1") });
                },
              },
              {
                id: "2",
                caption: "Lösche Verbindung zu: " + "2",
                action: () => {
                  showToast({ text: itx("index.action2", "action 2") });
                },
              },
            ];
            const task = {
              group: TaskGroup.DataInconsistency,
              title: itx("consumer.multipleConnections", "Verbraucher mit mehreren Verbindungen"),
              object: new Actor(),
              interactions: actions,
            };

            state.taskAndMessageManager.showTasks(task);
          }}
        >
          open test task
        </button> */}
      </div>
    );
  }

  async function openRecoveryBackup(backup: ProjectMetadata): Promise<void> {
    const blob = await loadProject(backup.id, true);
    updateToastProgress({ progress: 100 });

    if (blob) {
      const targetPath = backup.applicationMode === ApplicationMode.ConceptOne ? "/concept-one" : "/app";
      navigate(targetPath);
    } else {
      await showToast({
        isError: true,
        text: itx("ProjectSelection.autobackupCouldNotBeOpened", "Das Autobackup konnte nicht geöffnet werden."),
      });
    }
  }

  function confirmDeleteRecoveryBackup(backup: ProjectMetadata): void {
    state.dialogManager.openDialog(
      <ConfirmDialog
        className="dialog-standard"
        title={itx("ProjectSelection.deleteAutobackup", "Autobackup löschen?")}
        text={`${itx(
          "ProjectSelection.confirmDeleteAutobackupProject",
          `Soll das Autobackup des Projekts ${backup.name}`,
          { T1: backup.name },
        )}\n${itx("ProjectSelection.confirmDeleteAutobackupQuestion", "wirklich gelöscht werden?")}`}
        okCaption={itx("yes", "Ja")}
        abortCaption={itx("no", "Nein")}
        onOkAsync={async () => {
          const snapshotPosition = backup.id.indexOf("-snapshot");
          const projectId = snapshotPosition > 0 ? backup.id.substring(0, snapshotPosition) : undefined;
          const autobackupsDeleted = projectId
            ? await universalstorageHandler.localStorageHandler.deleteProjectAutobackups(projectId)
            : false;

          if (autobackupsDeleted) {
            setRecoveryBackups((backups) =>
              backups.filter((item) => {
                const itemSnapshotPosition = item.id.indexOf("-snapshot");
                const itemProjectId = itemSnapshotPosition > 0 ? item.id.substring(0, itemSnapshotPosition) : undefined;
                return itemProjectId !== projectId;
              }),
            );
            await showToast({
              isSuccess: true,
              text: itx("ProjectSelection.autobackupDeleted", "Das Autobackup wurde gelöscht."),
            });
            return true;
          }

          await showToast({
            isError: true,
            text: itx("ProjectSelection.autobackupCouldNotBeDeleted", "Das Autobackup konnte nicht gelöscht werden."),
          });
          return false;
        }}
      />,
    );
  }

  function formatRelativeBackupTime(date: Date | undefined): string {
    if (!date || Number.isNaN(date.getTime())) return "";

    const differenceSeconds = (date.getTime() - Date.now()) / 1000;
    const absoluteSeconds = Math.abs(differenceSeconds);
    const relativeTime = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

    if (absoluteSeconds < 60) return relativeTime.format(Math.round(differenceSeconds), "second"); // no itx
    if (absoluteSeconds < 60 * 60) return relativeTime.format(Math.round(differenceSeconds / 60), "minute"); // no itx
    if (absoluteSeconds < 24 * 60 * 60) return relativeTime.format(Math.round(differenceSeconds / 3600), "hour"); // no itx
    if (absoluteSeconds < 30 * 24 * 60 * 60) {
      return relativeTime.format(Math.round(differenceSeconds / (24 * 3600)), "day"); // no itx
    }
    if (absoluteSeconds < 365 * 24 * 60 * 60) {
      return relativeTime.format(Math.round(differenceSeconds / (30 * 24 * 3600)), "month"); // no itx
    }
    return relativeTime.format(Math.round(differenceSeconds / (365 * 24 * 3600)), "year"); // no itx
  }

  if (!recoveryCheckComplete) {
    return (
      <div className="App" key={state.settings.uiLanguage}>
        <FullSceenButton scope="project-selection" />
        <HeadBar projectSelection />
        <Loading text={itx("homePage.projectsLoadingWait", "Projekte werden geladen ... Bitte warten").split("^")[0]} />
      </div>
    );
  }

  if (!recoveryDismissed && recoveryBackups.length > 0) {
    return (
      <div className="App" key={state.settings.uiLanguage}>
        <FullSceenButton scope="project-selection" />
        <HeadBar projectSelection />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            padding: "40px 20px",
          }}
        >
          <h2>
            {itx(
              "ProjectSelection.projectsNotSavedBeforeExit",
              "ESTADA wurde ohne zu speichern beendet oder abgebrochen",
            )}
          </h2>

          <Typography
            component="div"
            variant="body1" // no itx
            style={{ maxWidth: "700px", marginTop: "8px", marginBottom: "8px", lineHeight: 1.6 }}
          >
            <div>
              {itx("ProjectSelection.automaticBackupsAvailable", "Es gibt automatisch gespeicherte Zwischenstände.")}
            </div>
            <div>
              {itx(
                "ProjectSelection.latestAutobackupCanBeOpened",
                "Der letzte kann hier direkt zur Weiterbearbeitung geöffnet werden.",
              )}
            </div>
            <div style={{ marginTop: "8px" }}>
              {itx(
                "ProjectSelection.snapshotsAvailableInProjectSelection",
                "In der Projektauswahl sind die einzelnen Snapshots und Autobackups der Projekte zur Ansicht und Auswahl verfügbar.",
              )}
            </div>
          </Typography>
        </div>
        <div
          className={"projectCardContainer"} // no itx
          style={{
            height: "100%",
            width: "100%",
            margin: "5px 0px",
            overflow: "auto",
          }}
        >
          <div
            className="projectCardFlexBox"
            style={{
              display: "grid",
              gridTemplateColumns: "max-content",
              justifyContent: "center",
              gap: "20px",
              height: "max-content",
            }}
          >
            {recoveryBackups.map((backup) => (
              <div
                key={backup.id}
                className="projectSelectionCard"
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  width: "auto",
                  minWidth: "350px",
                  maxWidth: "none",
                  flexShrink: 0,
                  height: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <CardContent>
                  <Typography variant="h5" component="div">
                    {backup.name}
                  </Typography>
                  <Typography
                    variant="body1" // no itx
                    title={backup.lastModifiedOnServer?.toLocaleString()}
                    style={{ marginTop: "6px", opacity: 0.8 }}
                  >
                    <i className="las la-clock" style={{ marginRight: "7px", fontSize: "1em" }} />
                    {itx("ProjectSelection.lastSaved", "Zuletzt gespeichert")}:{" "}
                    <span style={{ textTransform: "capitalize" }}>
                      {formatRelativeBackupTime(backup.lastModifiedOnServer)}
                    </span>
                  </Typography>
                  <Typography
                    variant="body1"
                    title={backup.localCopied?.toLocaleString()}
                    style={{ marginTop: "6px", opacity: 0.8 }}
                  >
                    <i className="las la-clock" style={{ marginRight: "7px", fontSize: "1em" }} />
                    {itx("ProjectSelection.backupCreated", "Backup von")}:{" "}
                    <span style={{ textTransform: "capitalize" }}>{formatRelativeBackupTime(backup.localCopied)}</span>
                  </Typography>
                </CardContent>
                <div
                  style={{
                    display: "inline-flex",
                    alignSelf: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "20px",
                    width: "max-content",
                    padding: "8px",
                  }}
                >
                  <button
                    className="button"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={() => confirmDeleteRecoveryBackup(backup)}
                  >
                    <i className="las la-trash" /> {itx("ProjectSelection.deleteAutobackup", "Autobackup löschen")}
                  </button>
                  <button
                    className="button"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={() => void openRecoveryBackup(backup)}
                  >
                    <i className="las la-edit" /> {itx("ProjectSelection.openAutobackup", "Autobackup öffnen")}
                  </button>
                </div>
              </div>
            ))}

            <div className="projectSelectionCard" style={{ width: "auto", height: "auto" }}>
              <CardContent>
                <Typography variant="h5" component="div">
                  {itx("ProjectSelection.keepAutobackups", "Autobackups nicht antasten")}
                </Typography>
              </CardContent>
              <CardActions style={{ justifyContent: "flex-end" }}>
                <button className="button" onClick={() => setRecoveryDismissed(true)}>
                  {itx("ProjectSelection.continueToProjectSelection", "Zur Projektauswahl")}
                </button>
              </CardActions>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /////////////////////////////////////////////////////////////////////////////
  return (
    <div className="App" key={state.settings.uiLanguage}>
      <FullSceenButton scope="project-selection" />
      <HeadBar projectSelection />
      {/* <AboBanner /> */}
      <FirefoxAlert />
      {/* {isUiModeDeveloper() && ( */}
      {true && (
        // Top toolbar with search and sorting controls.
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            width: isUiModeAdmin() ? "100%" : "390px", // no itx
            alignSelf: "center",
          }}
        >
          {/* Project search field available for all users. */}
          <div
            className={"projSearchInput"} // no itx
            style={{ padding: "3px", width: "100%" }}
          >
            <input
              className="input"
              key={"projSearchInput"} // no itx
              placeholder={state.translate("homePage.searchProject", "Search project(s)...")}
              defaultValue={projSearchValue}
              // ref={emailElm}
              style={{ width: "100%" }}
              id="search"
              type="text"
              maxLength={state.maxCharLimit.text}
              onChange={(ev) => {
                setProjSearchValue(ev.target.value);
              }}
            />
          </div>
          {isUiModeAdmin() && (
            // Admin-only filter for project owner name or email.
            <div
              className={"projSearchFromNameInput"} // no itx
              style={{ padding: "3px", width: "100%" }}
            >
              <input
                className="input"
                key={"projSearchInput"} // no itx
                placeholder={state.translate(
                  "homePage.searchProjectFromName",
                  "Search project(s) from Name or Email...",
                )}
                defaultValue={projSearchNameOrEmailValue}
                // ref={emailElm}
                style={{ width: "100%" }}
                id="search"
                type="text"
                maxLength={state.maxCharLimit.text}
                onChange={(ev) => {
                  setProjSearchNameValue(ev.target.value);
                }}
              />
            </div>
          )}
          {!isUiModeAdmin() && (
            // Compact toggle for end users: favorite-first <-> latest-first.
            <button
              className="button circle"
              style={{ margin: "3px", position: "absolute", right: "2px" }}
              data-tooltip-id="tv-tooltip"
              data-tooltip-content={
                sortMode == "date"
                  ? itx("ProjectSelection.sortfavoritefirst", "sort favorite first")
                  : itx("ProjectSelection.sortbydate", "sort by date")
              }
              data-tooltip-place="bottom"
              onClick={() => {
                // Non-admin users toggle directly between the two requested sorting modes.
                const nextSortMode = sortMode == "favorite" ? "date" : "favorite"; // no itx
                setSortAndApply(nextSortMode);
              }}
              title={
                sortMode == "date"
                  ? itx("ProjectSelection.sortbydate", "sort by date")
                  : itx("ProjectSelection.sortfavoritefirst", "sort favorite first")
              }
              aria-label={
                sortMode == "date"
                  ? itx("ProjectSelection.sortbydate", "sort by date")
                  : itx("ProjectSelection.sortfavoritefirst", "sort favorite first")
              }
            >
              <i className={sortMode == "date" ? "las la-calendar-alt" : "las la-star"} />
            </button>
          )}
          {isUiModeAdmin() && (
            // Full sorting control for admins.
            <select
              className="select filterProjectCards"
              style={{ margin: "3px", width: "150px" }}
              onChange={(ev) => {
                const value = ev.target.value;
                sortElements.get(value)?.onChange();
              }}
              value={sortMode}
            >
              {sortOptions}
            </select>
          )}
        </div>
      )}
      {/* Main scroll area for project cards and loading states. */}
      <div
        className={"projectCardContainer"} // no itx
        style={{
          height: "100%",
          width: "100%",
          margin: "5px 0px",
          overflow: "auto",
        }}
      >
        {uploadRunning && (
          // Prevents interactions while uploads/imports are still in progress.
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backgroundColor: "#fffa",
              zIndex: "10",
            }}
          >
            <Loading text={state.translate("homePage.uploadIsRunning", "Uploading...")} />
          </div>
        )}
        <div
          className="projectCardFlexBox"
          style={{
            height: "maxContent",
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "20px",
            paddingBottom: "20px",
            gap: "20px",
          }}
        >
          {/* Main project list including verification card and all visible projects. */}
          {!userVerified && <VerificationProjectCard setUserVerified={setUserVerified} />}
          {projectsLoaded &&
            projects &&
            projects.map((projectMetadata: ProjectMetadata, index: any) => {
              return (
                displaySearchProject(projectMetadata) && (
                  <div key={index}>
                    {true && (
                      <ProjectCard
                        key={projectMetadata.id}
                        setFavorieProject={setFavoriteProject}
                        id={projectMetadata.id}
                        projectMetadata={projectMetadata}
                        setUploadRunning={setUploadRunning}
                        fetchProjects={fetchProjects}
                        isExpanded={expandedProjects}
                        setExpanded={setExpandedProject}
                        projectHandleling={projectHandleling}
                        latestAutobackup={recoveryBackups.find((backup) => {
                          const snapshotPosition = backup.id.indexOf("-snapshot");
                          return (
                            snapshotPosition > 0 && backup.id.substring(0, snapshotPosition) === projectMetadata.id
                          );
                        })}
                        // handleDragProjectCard={handleDragProjectCard}
                        // handleDropProjectCard={handleDropProjectCard}
                      />
                    )}
                  </div>
                )
              );
            })}

          {!projectsLoaded && navigator.onLine && (
            <Loading
              text={itx("homePage.projectsLoadingWait", "Projekte werden geladen ... Bitte warten").split("^")[0]}
            />
          )}
        </div>
        <div
          className="projectCardFlexBox"
          style={{
            height: "maxContent",
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: "20px",
            gap: "20px",
          }}
        >
          {/* Secondary section with create/import actions and optional example cards. */}
          {projectsLoaded && <NewProjectCard />}
          {SHOW_CONCEPT_ONE_ACCESS && projectsLoaded && <ConceptOneProjectCard />}
          {false && projectsLoaded && showNewProjectCard() && <GPTProjectCard projectHandleling={projectHandleling} />}
          {(isUiModeDeveloper() || projects.length == 0) && projectsLoaded && navigator.onLine && (
            <ExampleProjectCard projectHandleling={projectHandleling} />
          )}

          <div
            style={{
              position: "relative",
              display: "block",
              width: "100%",
              height: "/0px",
            }}
          ></div>
        </div>
        {/* <button
          className="button"
          style={{ position: "absolute", bottom: "50px", right: "50px" }}
          onClick={() => {
            getAllSignalDetections();
          }}
        >
          downloadAllTempl
        </button> */}
        <div
          style={{
            position: "absolute",
            bottom: "0px",
            display: "block",
            width: "100%",
            height: "50px",
            backgroundImage:
              "linear-gradient(to bottom,rgba(255, 255, 255, 0),rgba(255, 255, 255, 0.7) 50%,var(--white1) 100%)",
          }}
        ></div>
      </div>
      {/* Extra developer actions stay outside the scroll container. */}
      {isUiModeDeveloper() && <DownloadEmailsButtons />}

      {projectsLoaded && navigator.onLine && (
        // Lightweight footer with the current number of visible projects.
        <div style={{ position: "absolute", left: "10px", bottom: "10px" }}>
          {projects.length} {itx("project(s)")}
        </div>
      )}
      {/* <DialogHost />
      <Toasts />
      <Tooltip id="tv-tooltip"  style={{ maxWidth: "50vw" }}/> */}

      <GlobalWaitIndicator />
    </div>
  );
}
